import React, { Component } from 'react';
import { ErrorInfo } from './ErrorInfo';
// @ts-ignore
import safeJsonStringify from 'safe-json-stringify';
import {
  initErrorLogging,
  startErrorLoggingUpload,
  useErrorLogging,
} from './ErrorLogging';
import { useAppConfig } from '../hooks/useAppConfig';
import { getAppInfo } from '../utils/getAppInfo';
import { downloadJson, takeScreenshot } from '../types/ReactAppLog';
import { Buffer } from 'buffer';
import copy from 'copy-to-clipboard';
import { showMessage, showMessageWithCopyButton } from './SystemToastr';

type State = {
  hasError: boolean;
  error?: any;
  errorInfo?: any;
  creatingLog: boolean;
};

export type GlobalAppError = {
  message: string;
  description?: string;
  infoStack?: string;
  infoError?: string;
  intoComponentError?: string;
};

/**
 * Inicializace globálního logování do lokálního úložiště se spuštěným asynchrinním uploadem.
 */
const appInitWebGlobalErrorHandling = () => {
  const errorLogging = useErrorLogging();
  let appConfig = useAppConfig();
  // console.log('@@@ INIT web global error handling');

  initErrorLogging(appConfig.serverContextPath).then(() => {
    window.addEventListener('error', event => {
      if (event.error?.stack) {
        errorLogging.logError(event.error.stack);
      } else {
        errorLogging.logError(
          `${event?.message}\n    at ??? (${event?.filename}:${event?.lineno}:${event?.colno})`
        );
      }
    });

    window.addEventListener('unhandledrejection', event => {
      if (event.reason) {
        if (typeof event.reason === 'string') {
          errorLogging.logError(`unhandledrejection - ${event.reason}`);
        } else if (event.reason?.stack) {
          errorLogging.logError(event.reason.stack);
        } else {
          errorLogging.logError(safeJsonStringify(event.reason));
        }
      } else {
        errorLogging.logError('unhandledrejection - no reason');
      }
    });

    startErrorLoggingUpload();
  });
};

export class ErrorBoundary extends Component<
  React.PropsWithChildren<{
    renderCustomError?: (err: GlobalAppError) => React.ReactNode;
  }>,
  State
> {
  state: State = { hasError: false, creatingLog: false };

  static getDerivedStateFromError(_error: any) {
    return { hasError: true };
  }

  constructor(props: any) {
    super(props);

    appInitWebGlobalErrorHandling();
  }

  createAppLog = async () => {
    // App info
    let appInfo = getAppInfo();

    // Screenshot
    const screenshot = await takeScreenshot();
    for (const error of screenshot.errors) {
      appInfo.messages.push(`[ERROR] ${error}`);
    }
    if (screenshot.base64Image) {
      let base64 = screenshot.base64Image;
      base64 = base64.substring(base64.indexOf('base64,') + 7);

      appInfo.files.push({
        name: `screenshot-${screenshot.type}.png`,
        content: base64,
      });
      // downloadBase64ImageImage(screenshot.base64Image);
    }

    // Server logs
    let appConfig = useAppConfig();
    try {
      const serverLogs = await fetch(
        `${appConfig.serverContextPath}/api/react-app/server-log`
      );
      let arrayBuffer = await serverLogs.arrayBuffer();
      // @ts-ignore
      const val = Buffer.from(arrayBuffer).toString('base64');
      appInfo.files.push({
        name: 'server-log.txt',
        content: val,
      });
    } catch (err) {
      appInfo.messages.push(`[ERROR] Chyba získání logu serveru: ${err}`);
    }

    // console.log('XXXXXXXX', appInfo);

    // Nejde to rovnou do shránky, protože top prohlížeč zablokuje pokud trvá načítání dat moc dlouho, pak už nechodí command copy do schránky
    // if (
    //   copy(JSON.stringify(appInfo), {
    //     debug: true,
    //     message: 'Zkopírování do schránky: #{key}, Enter',
    //   })
    // ) {
    //   showMessage('Informace o aplikaci byly zkopírovány do schránky.');
    //
    //   this.setState({
    //     ...this.state,
    //     creatingLog: false,
    //   });
    // } else {
    // }

    const appInfoStr = JSON.stringify(appInfo);
    if (appInfoStr.length > 10 * 1024 * 1024) {
      downloadJson(appInfo);
      showMessage(
        'Soubor s informacemi o aplikaci byl uložen na disk, protože je větší než 10 MB.'
      );
    } else {
      showMessageWithCopyButton('Informace o aplikaci byly načteny.', () => {
        copy(appInfoStr, {
          debug: true,
          message: 'Zkopírování do schránky: #{key}, Enter',
        });

        this.setState({
          ...this.state,
          creatingLog: false,
        });
      });
    }
  };

  handleKeyDown = async (event: KeyboardEvent) => {
    // console.log(
    //   `LL: ${this.state.creatingLog}, Key pressed: ${event.key} ALT:${event.altKey} CTRL:${event.ctrlKey} SHIFT:${event.shiftKey}`
    // );

    if (!this.state.creatingLog) {
      // Windows - Ctrl+Shit+Alt+l nebo Ctrl+Shit+Alt+L
      const winShortcut =
        (event.key === 'L' || event.key === 'l') &&
        event.shiftKey &&
        event.ctrlKey &&
        event.altKey;

      // MAC - Meta+Shit+Alt+l nebo Meta+Shit+Alt+L
      const macShortcut =
        (event.key === 'L' || event.key === 'l') &&
        event.shiftKey &&
        event.metaKey &&
        event.altKey;

      if (winShortcut || macShortcut) {
        this.setState({
          ...this.state,
          creatingLog: true,
        });

        console.log('Vytváří se log...');
        await this.createAppLog();
      }
    }
  };

  handleKeyUp = () => {
    if (this.state.creatingLog) {
      this.setState({
        ...this.state,
        creatingLog: false,
      });
    }
  };

  componentDidCatch(error: any, errorInfo: any) {
    console.error(error, errorInfo);
    this.setState({ error, errorInfo, hasError: true });
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('keyup', this.handleKeyUp);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('keyup', this.handleKeyUp);
  }

  render() {
    const { renderCustomError } = this.props;

    if (this.state.hasError) {
      let message = 'Nastala chyba v aplikaci';
      let description = this.state.error?.message;

      let infoStack;
      let infoError;
      let intoComponentError;

      if (this.state.error && this.state.error.stack) {
        infoStack = this.state.error.stack.toString();
      }
      if (this.state.error) {
        infoError = JSON.stringify(this.state.error, null, 4);
      }

      if (this.state.errorInfo) {
        if (this.state.errorInfo.componentStack) {
          intoComponentError = this.state.errorInfo.componentStack;
        } else {
          intoComponentError = JSON.stringify(this.state.errorInfo, null, 4);
        }
      }

      const globalAppError: GlobalAppError = {
        message,
        description,
        infoError,
        infoStack,
        intoComponentError,
      };

      if (renderCustomError) {
        return renderCustomError(globalAppError);
      } else {
        const children = (
          <>
            <pre>{globalAppError.infoStack}</pre>
            <pre>{globalAppError.infoError}</pre>
            <pre>{globalAppError.intoComponentError}</pre>
          </>
        );

        return (
          <ErrorInfo
            message={globalAppError.message}
            description={globalAppError.description}
            refreshAction
          >
            {children}
          </ErrorInfo>
        );
      }
    }

    return this.props.children;
  }
}
